import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Search, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useShop } from "../store/ShopContext";
import { api, formatPrice } from "../lib/api";

const SUGGESTIONS = ["Sleek 2", "Spring", "Urban Glide", "Coaster", "Landou", "Husă ploaie"];

export const SearchOverlay = () => {
  const { searchOpen, setSearchOpen } = useShop();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!searchOpen) return;
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/products", { params: { q: q.trim(), limit: 8 } });
        setResults(data);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, searchOpen]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setSearchOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setSearchOpen]);

  const close = () => { setSearchOpen(false); setQ(""); };

  return (
    <AnimatePresence>
      {searchOpen && (
        <motion.div data-testid="search-overlay" className="fixed inset-0 z-50 bg-white/95 backdrop-blur-xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
          <div className="container-cc pt-8">
            <div className="flex items-center justify-end">
              <button data-testid="search-close-btn" aria-label="Închide" className="icon-btn" onClick={close}><X size={22} strokeWidth={1.5} /></button>
            </div>
            <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 }} className="max-w-3xl mt-10">
              <div className="flex items-center gap-4 border-b-2 border-[#0A0A0A] pb-3">
                <Search size={26} strokeWidth={1.2} />
                <input
                  data-testid="search-input"
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Caută un cărucior, un scaun auto, o colecție…"
                  className="flex-1 bg-transparent font-serif text-3xl sm:text-4xl font-light focus:outline-none placeholder:text-neutral-300"
                />
              </div>
              {q.trim().length < 2 ? (
                <div className="mt-8">
                  <p className="eyebrow mb-4">Sugestii</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid={`search-suggestion-${s.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setQ(s)} className="border border-black/15 px-4 h-9 text-sm hover:border-[#0A0A0A] transition-colors duration-200">{s}</button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-8">
                  <p className="eyebrow mb-4">{loading ? "Se caută…" : `${results.length} rezultate`}</p>
                  {!loading && results.length === 0 && (
                    <p data-testid="search-no-results" className="font-serif text-2xl text-[#737373]">Niciun produs găsit pentru „{q}”.</p>
                  )}
                  <ul className="divide-y divide-black/5">
                    {results.map((p) => (
                      <li key={p.slug}>
                        <Link to={`/produs/${p.slug}`} data-testid={`search-result-${p.slug}`} onClick={close} className="flex items-center gap-5 py-3 group">
                          <div className="surface h-16 w-16 flex items-center justify-center p-1.5 shrink-0"><img src={p.images[0]} alt={p.name} className="product-img max-h-full object-contain" /></div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{p.name}{p.color ? ` · ${p.color}` : ""}</p>
                            <p className="text-xs text-[#737373]">{p.collection}</p>
                          </div>
                          <span className="font-semibold text-sm">{formatPrice(p.price)}</span>
                          <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
